import React, { useState } from 'react';
import {
  Button, Dialog, Box, Typography, Divider, CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckedIcon from 'Images/checked_icon.svg';
import DeleteIcon from 'Images/delete_icon.svg';

const FONT = "'Open Sans', sans-serif";

const PowerIcon = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 17.3 19.003">
    <path d="M16.056,5.944a7.65,7.65,0,1,1-10.821,0M10.649,2v8.5" transform="translate(-1.995 -1)" fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const KeyIconSvg = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 17.26 17.26">
    <g transform="translate(-548.947 -301.81)">
      <path d="M6.678,14.475l1.559-1.559H9.8l1.06-1.06A5.068,5.068,0,1,0,7.741,8.743L2,14.475v3.119H5.119l1.559-1.559Z" transform="translate(547.697 300.727)" fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M17.559,6.78A.78.78,0,1,1,16.78,6,.78.78,0,0,1,17.559,6.78Z" transform="translate(544.612 299.844)" fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </g>
  </svg>
);

const LockIconSvg = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 15.115 16.625">
    <g transform="translate(-549.936 -404.211)">
      <path d="M4.513,11H15.1a1.513,1.513,0,0,1,1.513,1.513v5.295A1.513,1.513,0,0,1,15.1,19.32H4.513A1.513,1.513,0,0,1,3,17.808V12.513A1.513,1.513,0,0,1,4.513,11Z" transform="translate(547.686 400.766)" fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M7,8.808V5.782a3.782,3.782,0,1,1,7.564,0V8.808" transform="translate(546.712 402.961)" fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </g>
  </svg>
);

const ResetMfaIconSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 17.469 14.115">
    <g transform="translate(-548.759 -354.464)">
      <path d="M1,4V8.186H5.186m11.163,6.977V10.977H12.163" transform="translate(548.819 351.941)" fill="none" stroke="#007BC6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M14.6,7.187A6.279,6.279,0,0,0,4.237,4.842L1,7.884m15.349,2.791-3.237,3.042a6.279,6.279,0,0,1-10.36-2.344" transform="translate(548.819 352.242)" fill="none" stroke="#007BC6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </g>
  </svg>
);

export default function ConfirmDialog({
  open,
  title,
  content, message,
  highlight, contentSuffix,
  handleClose, onClose,
  handleClick, onConfirm,
  loading,
  confirmLabel,
  confirmDisabled,
  variant,
  children,
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const hasExternalLoading = loading !== undefined;
  const isLoading = (loading || false) || internalLoading;

  const dialogOpen = open !== undefined ? open : true;
  const closeHandler = handleClose || onClose || (() => {});
  const confirmHandler = handleClick || onConfirm || (() => {});
  const bodyText = content || message;
  const titleLower = title?.toLowerCase() || '';

  const handleConfirmClick = async () => {
    const result = confirmHandler();
    if (result && typeof result.then === 'function') {
      setInternalLoading(true);
      try {
        await result;
        closeHandler();
      } finally {
        setInternalLoading(false);
      }
    } else if (!hasExternalLoading) {
      closeHandler();
    }
  };

  let iconEl;
  let iconBg;
  let actionBg;
  let actionHover;
  let resolvedLabel;

  if (variant === 'delete' || (!variant && (titleLower.includes('supprimer') || titleLower.includes('suppression')))) {
    iconBg = '#f4dae0';
    iconEl = <img src={DeleteIcon} alt="" style={{ width: 20, height: 20 }} />;
    actionBg = '#EF456E';
    actionHover = '#d63a5e';
    resolvedLabel = 'Supprimer';
  } else if (variant === 'validate' || variant === 'save') {
    iconBg = '#d6f4eb';
    iconEl = <img src={CheckedIcon} alt="" style={{ width: 20, height: 20 }} />;
    actionBg = '#0AD69F';
    actionHover = '#09c090';
    resolvedLabel = 'Confirmer';
  } else if (variant === 'leave') {
    iconBg = '#FFF8E1';
    iconEl = <WarningAmberIcon sx={{ fontSize: 20, color: '#E67E22' }} />;
    actionBg = '#E67E22';
    actionHover = '#CA6F1E';
    resolvedLabel = 'Quitter';
  } else {
    const isResetPassword = titleLower.includes('mot de passe');
    const isResetMfa = !isResetPassword && titleLower.includes('réinitialiser') && titleLower.includes('mfa');
    const isDisableMfa = titleLower.includes('désactiver') && titleLower.includes('mfa');
    const isDeactivate = !isResetPassword && !isDisableMfa && titleLower.includes('désactiver');
    const isActivate = !isDeactivate && !isResetPassword && !isDisableMfa && titleLower.includes('activer');
    const isReset = !isResetPassword && !isResetMfa && titleLower.includes('réinitialiser');

    if (isResetPassword || isReset) {
      iconBg = 'rgba(0, 123, 198, 0.21)';
      iconEl = <KeyIconSvg color="#007BC6" />;
      actionBg = '#007BC6';
      actionHover = '#005F9E';
      resolvedLabel = 'Réinitialiser';
    } else if (isResetMfa) {
      iconBg = 'rgba(0, 123, 198, 0.21)';
      iconEl = <ResetMfaIconSvg />;
      actionBg = '#007BC6';
      actionHover = '#005F9E';
      resolvedLabel = 'Réinitialiser';
    } else if (isDisableMfa) {
      iconBg = 'rgba(239, 69, 110, 0.21)';
      iconEl = <LockIconSvg color="#EF456E" />;
      actionBg = '#EF456E';
      actionHover = '#D63060';
      resolvedLabel = 'Désactiver';
    } else if (isDeactivate) {
      iconBg = 'rgba(239, 69, 110, 0.21)';
      iconEl = <PowerIcon color="#EF456E" />;
      actionBg = '#EF456E';
      actionHover = '#D63060';
      resolvedLabel = 'Désactiver';
    } else if (isActivate) {
      iconBg = '#E6FBF6';
      iconEl = <PowerIcon color="#0AD69F" />;
      actionBg = '#0AD69F';
      actionHover = '#08B888';
      resolvedLabel = 'Activer';
    } else {
      iconBg = '#FDECEA';
      iconEl = <PowerIcon color="#E53935" />;
      actionBg = '#E53935';
      actionHover = '#C62828';
      resolvedLabel = 'Confirmer';
    }
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={closeHandler}
      maxWidth="xs"
      fullWidth
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 37, 59, 0.28)' } } }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: '28px 32px',
          fontFamily: FONT,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          marginBottom: '370px',
        },
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '14px', mb: '16px',
      }}
      >
        <Box sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        >
          {iconEl}
        </Box>
        <Typography sx={{
          fontSize: '20px', fontWeight: 600, color: '#385576', fontFamily: FONT,
        }}
        >
          {title}
        </Typography>
      </Box>

      {bodyText && (
        highlight ? (
          <Typography sx={{
            fontSize: '14px', color: '#8397AE', fontFamily: FONT, mb: '20px', lineHeight: 1.6, fontWeight: 500,
          }}
          >
            {bodyText}
            {' '}
            <Box component="span" sx={{ color: actionBg, fontWeight: 700 }}>{highlight}</Box>
            {contentSuffix && (
              <>
                {' '}
                {contentSuffix}
              </>
            )}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: '14px', color: '#8397AE', fontFamily: FONT, mb: '20px', lineHeight: 1.6, fontWeight: 500, whiteSpace: 'pre-line',
          }}
          >
            {bodyText}
          </Typography>
        )
      )}

      {children && <Box sx={{ mb: '20px' }}>{children}</Box>}

      <Divider sx={{ mb: '20px', borderColor: '#F0F2F5' }} />

      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Button
          fullWidth
          disabled={isLoading}
          onClick={closeHandler}
          sx={{
            color: '#4D4D4D',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: FONT,
            border: '1px solid #DEE5EE',
            borderRadius: '8px',
            py: '10px',
            backgroundColor: '#fff',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.08)',
            '&:hover': { backgroundColor: '#F5F7FA' },
          }}
        >
          Annuler
        </Button>
        <Button
          fullWidth
          disabled={confirmDisabled || isLoading}
          onClick={handleConfirmClick}
          sx={{
            backgroundColor: actionBg,
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: FONT,
            borderRadius: '8px',
            py: '10px',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.08)',
            '&:hover': { backgroundColor: actionHover },
            '&.Mui-disabled': { backgroundColor: actionBg, color: '#fff', opacity: 0.5 },
          }}
        >
          {isLoading
            ? <CircularProgress size={15} sx={{ color: '#fff' }} />
            : (confirmLabel || resolvedLabel)}
        </Button>
      </Box>
    </Dialog>
  );
}
